import { defineStore } from 'pinia';
import axios from 'axios';
import { companyApi } from '../services/admin/companyApi';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: window.initialUser || null, // We might inject this from blade
        loading: false,
        isAuthenticated: !!window.initialUser,
        companies: []
    }),

    getters: {
        itemUser: (state) => state.user,
        isLoggedIn: (state) => !!state.user,
        defaultCompany: (state) => state.user?.default_company || null
    },

    actions: {
        async login(credentials) {
            this.loading = true;
            try {
                // Refresh the CSRF cookie before login — the session may have been
                // regenerated since page load (e.g. after a previous logout).
                await axios.get('/sanctum/csrf-cookie');

                const response = await axios.post('/api/login', credentials);

                // 3. Check for success
                if (response.data.status === 'success') {
                    this.user = response.data.user;
                    this.isAuthenticated = true;

                    // Update global window vars for legacy compatibility
                    if (this.user.role_names) window.userRoles = this.user.role_names;
                    if (this.user.all_permissions) window.userPermissions = this.user.all_permissions;

                    return this.user;
                }

                throw new Error(response.data.message || 'Login failed');

            } catch (error) {
                this.user = null;
                this.isAuthenticated = false;
                console.error('Login failed', error);
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async logout() {
            try {
                await axios.post('/api/logout');
            } catch (error) {
                console.error('Logout error', error);
            } finally {
                this.user = null;
                this.isAuthenticated = false;
                // Logout regenerates the session CSRF token server-side.
                // Refresh the XSRF-TOKEN cookie so the next POST (e.g. login) doesn't get 419.
                window.csrfReady = axios.get('/sanctum/csrf-cookie').catch(() => {});
            }
        },

        async fetchUser() {
            try {
                // Use new dedicated API endpoint
                const response = await axios.get('/api/user');

                // Require BOTH success status AND a non-null user object.
                // Defensive: if the server ever regresses and returns {status:'success', user:null},
                // we still treat it as unauthenticated — otherwise logout triggers an infinite
                // redirect loop between /login → dashboard → /login.
                if (response.data.status === 'success' && response.data.user) {
                    this.user = response.data.user;
                    this.isAuthenticated = true;

                    // Update global window vars for legacy compatibility
                    if (this.user.role_names) window.userRoles = this.user.role_names;
                    if (this.user.all_permissions) window.userPermissions = this.user.all_permissions;

                    return this.user;
                }
                throw new Error('Failed to fetch user');
            } catch (error) {
                this.user = null;
                this.isAuthenticated = false;
                throw error;
            }
        },

        /**
         * Merge partial user data into the store (e.g. after profile update).
         * Preserves existing fields like role_names, all_permissions, companies
         * that the profile endpoint may not return.
         */
        updateUser(partialUser) {
            if (!partialUser) return;
            this.user = { ...(this.user || {}), ...partialUser };
            this.isAuthenticated = true;
        },

        /**
         * Clear the profile pic in the store (e.g. after deletion).
         */
        clearProfilePic() {
            if (!this.user) return;
            this.user = { ...this.user, profile_pic: null, profile_pic_url: null };
        },

        async fetchCompanies() {
            try {
                const response = await companyApi.getUserCompanies();
                this.companies = response.data.data || [];
                return this.companies;
            } catch (error) {
                console.error('Failed to fetch companies:', error);
                return [];
            }
        },

        async switchCompany(companyId) {
            const response = await companyApi.switchCompany(companyId);

            if (response.data.status === 'success') {
                // Update default_company in the user state
                if (this.user) {
                    this.user.default_company = response.data.data.default_company;
                }
                // Update is_default flags in cached companies list
                this.companies = this.companies.map(c => ({
                    ...c,
                    is_default: c.id === companyId
                }));
                return response.data.data.default_company;
            }

            throw new Error(response.data.message || 'Failed to switch company');
        },

        addCompanyToList(company) {
            this.companies.push({ ...company, is_default: false });
        }
    }
});
